/**
 * useConsoleEvents — subscribe to the admin-console event stream.
 *
 * Opens a WebSocket to the chat service's /chat/ws/console endpoint and invokes
 * `onEvent(data)` for every pushed event (ESCALATION_EVENT, AGENT_PRESENCE, …).
 * This replaces timer-based polling on the Live Agent / Ticket Management pages:
 * the dashboard only re-fetches when the server says something actually changed.
 *
 * Features: AUTH handshake with the stored admin JWT, 25s keepalive ping, and
 * automatic reconnect with exponential backoff. Cleans up fully on unmount.
 *
 * Usage:
 *   useConsoleEvents((evt) => { if (evt.type === "ESCALATION_EVENT") reload(); });
 */
import { useEffect, useRef } from "react";
import { WS_URL } from "../config/endpoints";

export default function useConsoleEvents(onEvent) {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    const wsBase = WS_URL;
    if (!wsBase) return undefined;

    let socket = null;
    let pingId = null;
    let reconnectId = null;
    let closed = false;
    let attempts = 0;

    const connect = () => {
      if (closed) return;
      try {
        socket = new WebSocket(`${wsBase}/chat/ws/console`);
      } catch {
        return;
      }

      socket.onopen = () => {
        attempts = 0;
        const token = sessionStorage.getItem("token");
        socket.send(JSON.stringify({ type: "AUTH", token }));
        pingId = setInterval(() => {
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "PING" }));
          }
        }, 25_000);
      };

      socket.onmessage = (e) => {
        let data;
        try { data = JSON.parse(e.data); } catch { return; }
        if (!data || data.type === "PONG" || data.type === "READY") return;
        if (data.error) return;
        cbRef.current?.(data);
      };

      socket.onclose = () => {
        if (pingId) clearInterval(pingId);
        if (closed) return;
        attempts += 1;
        const delay = Math.min(1000 * 2 ** attempts, 15_000);
        reconnectId = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        try { socket.close(); } catch { /* noop */ }
      };
    };

    connect();

    return () => {
      closed = true;
      if (pingId) clearInterval(pingId);
      if (reconnectId) clearTimeout(reconnectId);
      try { if (socket) socket.close(); } catch { /* noop */ }
    };
  }, []);
}
